import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { fetchGuestGuide } from '../../../../../api/stay-guide-public-client';
import { GuideOpenBody, GuidePinLockedBody, GuideUpcomingBody } from '../../../../../interfaces/stay-guide';
import { AutoHeight, Staleable, StateSwitch } from '../../../../../motion';
import { VisuallyHidden } from '../../../../../style-utils/text';
import { LogoMark } from '../../../../logo/logo-mark';
import { LoadError } from '../../../dashboard/dashboard-shared-components/load-state';
import {
  BrandRow,
  Card,
  FieldSkeleton,
  HeadingSkeleton,
  Shell,
  SkeletonGroup,
  SkeletonOption,
  Wordmark,
} from '../../../../onboarding/onboarding.styled';
import { Actions, Primary } from '../../../../onboarding/stay/public-stay-page.styled';
import { daysBetween } from '../../../../../function-utils/building-time';
import { formatStayDate, nightsGuestsLine, PageStateView, useLimitedRetry } from '../../../../onboarding/stay/stay-steps';
import { t } from '../../../../onboarding/stay/stay-strings';
import { Header, Line, StrongLine } from '../guest-checkin.styled';
import { GuideGuestEffects, GuideNav, GuideNavContext, GuideTarget, TITLE_ID, TOP_ID } from './guide-frame';
import { At, BuildingClockNote, DeviceClockNote, deviceTimeOf, longDateIn, timeIn } from './guide-format';
import { GuideOpenView } from './guide-open-view';
import { GuidePinGate } from './guide-pin-gate';
import { isGuideSection } from './guide-state';
import { Frame, SkeletonCards, Stack, Title } from './guide.styled';
import { useGuideBoundaries } from './use-guide-boundaries';

const L = 'en' as const;

/**
 * THE STAY GUEST'S GUIDE — /s/:token/guide and /s/:token/guide/<section>
 * (Lobbero Stays plan S7.7).
 *
 * The server decides everything (the gate, the PIN, the release, the stage);
 * this page renders its answer and asks again at the answer's own boundaries
 * (use-guide-boundaries.ts). One route entry for the hub and the five
 * sections (`/s/:token/guide/*`), so moving between sections never refetches.
 *
 *   loading      a skeleton shaped like the hub
 *   invalid      S6's invalid-link screen (one answer for every dead link)
 *   limited      "Too many tries…" — no retry for the limiter's minute
 *   error        "We could not load your guide." + Try again
 *   pin_required the PIN gate
 *   pin_locked   "Too many wrong PINs. Try again at 15:40."
 *   upcoming     "Your guide opens on Friday 24 October at 15:00." + the stay
 *   ended        "Thank you for staying with us, Ana. This guide is now closed."
 *   open         the hub or a section, in the frame
 *
 * NEVER PERSISTED: no guide value in localStorage, the title, the URL or the
 * console (no-persist.guard.test.ts); the token stays in the path it came in.
 */

const GuideShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Shell>
    <Card wide>
      <BrandRow>
        <LogoMark size={40} aria-hidden="true" />
        <Wordmark>Lobbero</Wordmark>
      </BrandRow>
      {children}
    </Card>
  </Shell>
);

export const HubSkeleton: React.FC = () => (
  <SkeletonGroup label={t(L, 'guide.loading')}>
    <HeadingSkeleton />
    <FieldSkeleton />
    <SkeletonCards aria-hidden="true">
      <SkeletonOption />
      <SkeletonOption />
      <SkeletonOption />
      <SkeletonOption />
    </SkeletonCards>
  </SkeletonGroup>
);

const PinLockedView: React.FC<{ body: GuidePinLockedBody }> = ({ body }) => {
  const until = body.pin.locked_until;
  if (!until) {
    return (
      <Title id={TITLE_ID} tabIndex={-1}>
        {t(L, 'guide.pinLockedLater')}
      </Title>
    );
  }
  const [before, after] = t(L, 'guide.pinLocked').split('{time}');
  // The building's clock when the server names the zone (it does); the device's otherwise.
  const zone = body.pin.zone;

  return (
    <Stack>
      <Title id={TITLE_ID} tabIndex={-1}>
        {before}
        <At iso={until}>{zone ? timeIn(until, zone) : deviceTimeOf(until)}</At>
        {after}
      </Title>
      {zone ? <BuildingClockNote zone={zone} /> : <DeviceClockNote />}
    </Stack>
  );
};

const UpcomingView: React.FC<{ body: GuideUpcomingBody }> = ({ body }) => {
  const [before, middle, after] = t(L, 'guide.upcoming').split(/\{date\}|\{time\}/);

  return (
    <Stack>
      <Header>
        {body.stay.listing_name ? <StrongLine>{body.stay.listing_name}</StrongLine> : null}
        <Line data-at={body.stay.check_in}>
          {t(L, 'datesLine', {
            from: formatStayDate(body.stay.check_in),
            to: formatStayDate(body.stay.check_out),
            nights: nightsGuestsLine(Math.max(0, daysBetween(body.stay.check_in, body.stay.check_out) || 0)),
          })}
        </Line>
      </Header>
      <Title id={TITLE_ID} tabIndex={-1}>
        {before}
        <At iso={body.opens_at}>{longDateIn(body.opens_at, body.zone)}</At>
        {middle}
        <At iso={body.opens_at}>{timeIn(body.opens_at, body.zone)}</At>
        {after}
      </Title>
      <BuildingClockNote zone={body.zone} />
    </Stack>
  );
};

const GuideForToken: React.FC<{ token: string; section: GuideTarget }> = ({ token, section }) => {
  const load = useCallback(() => fetchGuestGuide(token), [token]);
  const { screen, body, refreshing, reload } = useGuideBoundaries(load);
  const { canRetry, hold } = useLimitedRetry();
  const firstSection = useRef(true);

  useEffect(() => {
    if (screen === 'limited') hold();
  }, [screen, hold]);

  // A move between sections lands at the top, on the new page's title.
  useEffect(() => {
    if (firstSection.current) {
      firstSection.current = false;

      return;
    }
    const top = document.getElementById(TOP_ID);
    if (top && typeof top.scrollIntoView === 'function') top.scrollIntoView({ block: 'start' });
    const title = document.getElementById(TITLE_ID);
    if (title) title.focus();
  }, [section]);

  const nav = useMemo<GuideNav>(() => ({ preview: false, token, current: section, go: () => undefined }), [token, section]);

  const renderView = () => {
    if (screen === 'loading') return <HubSkeleton />;
    if (screen === 'invalid') return <PageStateView state="invalid" canRetry={false} onRetry={() => reload()} />;
    if (screen === 'limited') {
      return (
        <>
          <Title id={TITLE_ID} tabIndex={-1}>
            {t(L, 'guide.limited')}
          </Title>
          {canRetry ? (
            <Actions>
              <Primary type="button" onClick={() => reload()}>
                {t(L, 'tryAgain')}
              </Primary>
            </Actions>
          ) : null}
        </>
      );
    }
    if (screen === 'error' || !body) {
      return (
        <>
          <VisuallyHidden as="h1">{t(L, 'guide.documentTitle')}</VisuallyHidden>
          <LoadError message={t(L, 'guide.error')} onRetry={() => reload()} retryLabel={t(L, 'tryAgain')} />
        </>
      );
    }
    switch (body.state) {
      case 'pin_required':
        return (
          <GuidePinGate
            token={token}
            attemptsLeft={body.pin.attempts_left}
            onUnlocked={() => reload()}
            onLocked={() => reload()}
            onRefused={() => reload()}
          />
        );
      case 'pin_locked':
        return <PinLockedView body={body} />;
      case 'upcoming':
        return <UpcomingView body={body} />;
      case 'ended':
        return (
          <Title id={TITLE_ID} tabIndex={-1}>
            {body.stay.first_name
              ? t(L, 'guide.ended', { first: body.stay.first_name })
              : t(L, 'guide.endedNoName')}
          </Title>
        );
      case 'open':
      default:
        return (
          <GuideNavContext.Provider value={nav}>
            <Staleable stale={refreshing}>
              <GuideOpenView body={body as GuideOpenBody} section={section} />
            </Staleable>
          </GuideNavContext.Provider>
        );
    }
  };

  return (
    <GuideShell>
      <GuideGuestEffects />
      <AutoHeight>
        <StateSwitch state={screen}>
          {screen === 'open' ? renderView() : <Frame id={TOP_ID}>{renderView()}</Frame>}
        </StateSwitch>
      </AutoHeight>
    </GuideShell>
  );
};

export const GuestGuidePage: React.FC = () => {
  const params = useParams<{ token: string; '*': string }>();
  const token = params.token ?? '';
  const rest = (params['*'] || '').replace(/\/+$/, '');
  if (rest && !isGuideSection(rest)) return <Navigate to={`/s/${token}/guide`} replace />;
  const section: GuideTarget = rest && isGuideSection(rest) ? rest : 'hub';

  return <GuideForToken key={token} token={token} section={section} />;
};
